/** One tab-local reviewed candidate. Publication uncertainty owns its original
 * ticket/operation until reconciled; another review never substitutes for it. */
import { ApiError, reviewNewFeedback, reviewExistingFeedback, publishNewFeedback, publishExistingFeedback,
  isCurrentFeedbackPublicationScope } from './api';
import { refreshFeedbackPublication } from './queryClient';
import type { FeedbackReviewIntent, FeedbackPublicationIntent, FeedbackPublicationScope,
  FeedbackReviewResult, FeedbackPublicationResult } from './types';

export type FeedbackPublicationState =
  | { status: 'ready' | 'reviewing' | 'review-failed' | 'publishing' | 'unknown' | 'rejected' | 'unavailable' }
  | { status: 'reviewed'; review: FeedbackReviewResult }
  | { status: 'retired'; outcome: 'unknown' }
  | { status: 'accepted'; result: FeedbackPublicationResult };
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T;

function reviewAcknowledgement(value: FeedbackReviewResult): FeedbackReviewResult {
  if (!value || value.schema_version !== 'feedback-review-result.v1' || !uuid.test(value.review_ticket)
    || typeof value.expires_at !== 'string' || !Number.isFinite(Date.parse(value.expires_at))
    || !/(Z|[+-]\d{2}:\d{2})$/.test(value.expires_at)
    || Object.keys(value).sort().join(',') !== 'expires_at,review_ticket,schema_version') throw new Error('invalid_feedback_review');
  return { ...value };
}

function publicationAcknowledgement(value: FeedbackPublicationResult, intent: FeedbackPublicationIntent): FeedbackPublicationResult {
  if (!value || value.schema_version !== 'feedback-publication-result.v1' || !uuid.test(value.feedback_id)
    || !uuid.test(value.revision_id) || !Number.isInteger(value.version) || value.version < 1 || value.version > 2147483647
    || (intent.action === 'new' ? value.version !== 1 : value.feedback_id.toLowerCase() !== intent.feedbackId.toLowerCase()
      || (value.version !== intent.body.expected_version && value.version !== intent.body.expected_version + 1))
    || Object.keys(value).sort().join(',') !== 'feedback_id,revision_id,schema_version,version') throw new Error('invalid_feedback_publication');
  return { ...value };
}

export function captureFeedbackPublication(scope: FeedbackPublicationScope, candidate: FeedbackReviewIntent,
  recovery: { restoredPublication?: FeedbackPublicationIntent;
    beforePublish?: (intent: FeedbackPublicationIntent) => void } = {}) {
  const capturedScope = { ...scope };
  const original = clone(candidate);
  const current = () => isCurrentFeedbackPublicationScope(capturedScope);
  let state: FeedbackPublicationState = { status: recovery.restoredPublication ? 'unknown' : 'ready' };
  let reviewed: FeedbackReviewResult | null = null;
  let command: FeedbackPublicationIntent | null = recovery.restoredPublication ? clone(recovery.restoredPublication) : null;
  let unresolved = !!recovery.restoredPublication;
  let reviewing: Promise<FeedbackReviewResult> | null = null;
  let publishing: Promise<FeedbackPublicationResult> | null = null;
  const available = (signal?: AbortSignal) => {
    if (!current() || signal?.aborted) throw new DOMException('Publication context changed', 'AbortError');
  };

  async function runReview(signal?: AbortSignal) {
    state = { status: 'reviewing' };
    try {
      const options = { scope: capturedScope, signal };
      const request = clone(original);
      const value = request.action === 'new' ? await reviewNewFeedback(request.body, options)
        : await reviewExistingFeedback(request.feedbackId, request.body, options);
      reviewed = reviewAcknowledgement(value);
      command = null;
      state = { status: 'reviewed', review: reviewed };
      return { ...reviewed };
    } catch (error) {
      reviewed = null;
      state = { status: 'review-failed' };
      throw error;
    } finally { reviewing = null; }
  }

  async function runPublish(signal?: AbortSignal) {
    state = { status: 'publishing' };
    const intent = clone(command!);
    try {
      const options = { scope: capturedScope, signal };
      const value = intent.action === 'new' ? await publishNewFeedback(intent.body, options)
        : await publishExistingFeedback(intent.feedbackId, intent.body, options);
      const result = publicationAcknowledgement(value, intent);
      unresolved = false;
      state = { status: 'accepted', result };
      void Promise.resolve().then(() => {
        if (current()) return refreshFeedbackPublication(capturedScope, result.feedback_id, intent.body.workspace?.workspace_id);
      }).catch(() => {});
      return { ...result };
    } catch (error) {
      const code = error instanceof ApiError && error.detail && typeof error.detail === 'object' && 'code' in error.detail ? error.detail.code : null;
      if (code === 'feedback_operation_unresolved' || code === 'feedback_operation_retired'
        || !(error instanceof ApiError && error.status >= 400 && error.status < 500)) unresolved = true;
      state = code === 'feedback_operation_retired' ? { status: 'retired', outcome: 'unknown' }
        : { status: unresolved ? 'unknown' : 'rejected' };
      throw error;
    } finally { publishing = null; }
  }

  return {
    // A shared continuation may adopt a command dispatched by another controller.
    // Its later 4xx cannot prove that the earlier attempt was never accepted.
    adoptUnresolved(): void {
      if (!current() || state.status === 'accepted') return;
      unresolved = true;
      state = { status: 'unknown' };
    },
    getState(): FeedbackPublicationState { return current() ? clone(state) : { status: 'unavailable' }; },
    getCandidate(): FeedbackReviewIntent | null { return current() ? clone(original) : null; },
    getPublicationIntent(): FeedbackPublicationIntent | null { return current() && command ? clone(command) : null; },
    review(signal?: AbortSignal): Promise<FeedbackReviewResult> {
      try { available(signal); } catch (error) { return Promise.reject(error); }
      if (publishing || unresolved || state.status === 'accepted') return Promise.reject(new Error('publication_requires_original_reconciliation'));
      if (reviewing) return reviewing;
      reviewing = runReview(signal);
      return reviewing;
    },
    publish(operationId: string, signal?: AbortSignal): Promise<FeedbackPublicationResult> {
      try { available(signal); } catch (error) { return Promise.reject(error); }
      if (!uuid.test(operationId)) return Promise.reject(new Error('invalid_publication_operation'));
      if (command && command.body.operation_id.toLowerCase() !== operationId.toLowerCase()) return Promise.reject(new Error('publication_requires_original_operation'));
      if (publishing) return publishing;
      if (state.status === 'accepted') return Promise.resolve({ ...state.result });
      if (reviewing || (!command && !reviewed)) return Promise.reject(new Error('publication_requires_review'));
      if (!command) command = original.action === 'new'
        ? { action: 'new', body: { ...clone(original.body), schema_version: 'feedback-publish.v1', review_ticket: reviewed!.review_ticket, operation_id: operationId } }
        : { action: 'existing', feedbackId: original.feedbackId, body: { ...clone(original.body), schema_version: 'feedback-publish.v1', review_ticket: reviewed!.review_ticket, operation_id: operationId } };
      // A continuation owner must durably accept this exact command before
      // network dispatch. Storage failure is not an unknown server outcome.
      try { recovery.beforePublish?.(clone(command)); } catch (error) { return Promise.reject(error); }
      publishing = runPublish(signal);
      return publishing;
    },
  };
}
